import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import EmptyState from "../../../../../../assets/images/emptyState/store.svg";
import ArrowRight from "../../../../../../assets/images/icons/chevron-right.svg";
import ArrowDown from "../../../../../../assets/images/icons/arrow-down.svg";
import ArrowLeft from "../../../../../../assets/images/icons/arrow-left.svg";
import { groupBy } from "../../../../../../helpers/groupBy";
import { slugify } from "../../../../../../helpers/slugify";
import { numberWithCommas } from "../../../../../helpers";
import { getPublicStore } from "../../../../../../redux/publicStore/publicStoreAction";


export default function MerchandiseDrawer({
    data,
    onMerchandiseSelect,
    showAttributes,
    setShowAttributes,
    singleMerchandise,
    attributeFields,
    handleFormChange,
    addAttributeFields
}) {
    const dispatch = useDispatch();
    const [openCategories, setOpenCategories] = useState({})
    const [displayMerchandise, setDisplayMerchandise] = useState([]);
    const [selectedMerchandise, setSelectedMerchandise] = useState(null);
    let group = groupBy(displayMerchandise, "merchandise_categories");

    const toggleCategory = (categoryName) => {
        setOpenCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    };

    const handleMerchandiseClick = (merchandise) => {
        setSelectedMerchandise(merchandise);
        onMerchandiseSelect(merchandise);
    };

    const handleBackToMenu = () => {
        setShowAttributes(false);
        setSelectedMerchandise(null);
    };



    const getStoreMerchandises = async () => {
        try {
            const response = await dispatch(getPublicStore(data?.store_id));
            if (response) {
                setDisplayMerchandise(response?.data?.merchandise);
            }
        } catch (error) {
            console.error(error)
        }
    }



    useEffect(() => {
        if (data?.store_id) {
            getStoreMerchandises();
        }
        // eslint-disable-next-line
    }, [data?.store_id]);

    return (
        <div className="px-4 pt-4 pb-10 shadow shadow-lg rounded-xl ">
            {!data?.store_id ?
                <>
                    <p className="mb-5">Store Merchandise</p>
                    <div className="grid gap-5 items-center justify-center text-center mt-20 max-w-[300px] mx-auto">
                        <img src={EmptyState} className="w-[100px] h-[100px] mx-auto" alt="no data" />
                        <p className="text-lg text-[#151515] dark:text-white font-bold">Store Merchandise</p>
                        <p className="text-sm text-#9CA3AF dark:text-[#BCBCBC]">Select a store to browse available merchandise items and quickly suggest alternatives to customers.</p>
                    </div>
                </>
                : showAttributes && selectedMerchandise ?
                    <>
                        <div className="flex items-center gap-3 mb-5">
                            <button
                                type="button"
                                onClick={handleBackToMenu}
                                className="outline-none"
                            >
                                <img src={ArrowLeft} alt="back" className="w-[20px] h-[20px]" />
                            </button>
                            <p className="text-[#333333] dark:text-white font-medium">
                                {selectedMerchandise?.merchandise_name}
                            </p>
                        </div>
                        <MerchandiseAttributes
                            merchandise={singleMerchandise}
                            attributeFields={attributeFields}
                            handleFormChange={handleFormChange}
                            addAttributeFields={addAttributeFields}
                        />
                    </>
                    :
                    <>
                        <p className="mb-5 text-[#333333] dark:text-white">{data?.store_name && data?.store_name} Merchandise</p>
                        {Object.keys(group).length > 0 ? (
                            Object.keys(group).map((item, i) => (
                                <div key={i} className="mb-6 scroll-mt-[130px]" id={slugify(item)}>
                                    <div className="flex items-center justify-between gap-5 border-b-2 py-1 mb-1">
                                        <h2 className="text-sm text-[#344B67] dark:text-white capitalize">{item}</h2>
                                        <button
                                            type="button"
                                            className="outline-none"
                                            onClick={() => toggleCategory(item)}
                                        >
                                            <img
                                                src={ArrowDown}
                                                className={`w-[12px] h-[12px] dark:bg-white transition-transform duration-300 ${openCategories[item] ? "" : "rotate-180"
                                                    }`}
                                                alt="warning"
                                            />
                                        </button>
                                    </div>
                                    {openCategories[item] && group[item].map((merchandise) => (
                                        <MerchandiseMenu
                                            key={merchandise?.id}
                                            merchandise={merchandise}
                                            onMerchandiseClick={handleMerchandiseClick}
                                        />
                                    ))}
                                </div>
                            ))
                        ) : (
                            <p className="mb-2 text-xl font-bold tracking-tight text-black">
                                No available merchandise in this store
                            </p>
                        )}
                    </>
            }
        </div>
    )
}



const MerchandiseMenu = ({ merchandise, onMerchandiseClick }) => {
    return (
        <ul>
            <li
                className="py-1 sm:py-2 cursor-pointer"
                onClick={() => onMerchandiseClick(merchandise)}
            >
                <div className="flex gap-3 items-center justify-between gap-2 px-4">

                    <div className="w-full">

                        <p className="text-sm capitalize text-[#151515] dark:text-white mb-1 text-start font-medium max-w-sm line-clamp-2">
                            {merchandise?.merchandise_name}
                        </p>

                        {merchandise?.merchandise_description === null ? (
                            <p className="text-sm capitalize mb-1 text-[#151515] dark:text-[#BCBCBC] max-w-sm">
                                No description available
                            </p>
                        ) : (
                            <p className="text-sm capitalize mb-1 text-[#151515] dark:text-[#BCBCBC] max-w-sm line-clamp-1">
                                {merchandise?.merchandise_description}
                            </p>
                        )}

                        <p className="text-sm font-medium">
                            NGN {numberWithCommas(merchandise?.merchandise_amount)}
                        </p>
                    </div>

                    {merchandise?.merchandise_attributes &&
                        <div className="w-fit">
                            <img
                                src={ArrowRight}
                                alt="right"
                                className="cursor-pointer w-[18px] h-[18px]"
                            />
                        </div>}
                </div>
            </li>
            <div className="border-b-2"></div>
        </ul>
    )
}

const MerchandiseAttributes = ({ merchandise, attributeFields, handleFormChange, addAttributeFields }) => {
    const [selectedAttr, setSelectedAttr] = useState([]);
    const group = groupBy(merchandise?.attributes, "title");

    useEffect(() => {
        // Sync with parent's attributeFields
        const filtered = attributeFields.filter(field => field.attribute_id !== "");
        setSelectedAttr(filtered);
    }, [attributeFields]);

    if (!merchandise?.attributes || merchandise.attributes.length === 0) {
        return (
            <div className="text-center py-10">
                <p className="text-sm text-[#676565] dark:text-[#BCBCBC]">
                    No attributes available for this item
                </p>
            </div>
        );
    }

    const hasAttributeId = (id) =>
        selectedAttr.some((obj) => Number(obj.attribute_id) === Number(id));

    const handleChange = (event, attr) => {
        if (event.target.checked) {
            // Find first empty slot or add new
            const emptyIndex = attributeFields.findIndex(field => field.attribute_id === "");
            if (emptyIndex !== -1) {
                // Update existing empty slot
                handleFormChange(emptyIndex, {
                    target: { name: "attribute_id", value: attr.id.toString() }
                });
                handleFormChange(emptyIndex, {
                    target: { name: "quantity", value: "1" }
                });
            } else {
                // No empty slot, add new field first
                addAttributeFields();
                // Then update it in next tick
                setTimeout(() => {
                    const newIndex = attributeFields.length;
                    handleFormChange(newIndex, {
                        target: { name: "attribute_id", value: attr.id.toString() }
                    });
                    handleFormChange(newIndex, {
                        target: { name: "quantity", value: "1" }
                    });
                }, 10);
            }
        } else {
            // Remove from list - set to empty values
            const index = attributeFields.findIndex(
                field => Number(field.attribute_id) === Number(attr.id)
            );
            if (index !== -1) {
                handleFormChange(index, {
                    target: { name: "attribute_id", value: "" }
                });
                handleFormChange(index, {
                    target: { name: "quantity", value: "" }
                });
            }
        }
    };

    const handleAddAttrCount = (id) => {
        const index = attributeFields.findIndex(
            field => Number(field.attribute_id) === Number(id)
        );
        if (index !== -1) {
            const currentQty = Number(attributeFields[index].quantity) || 1;
            handleFormChange(index, {
                target: { name: "quantity", value: (currentQty + 1).toString() }
            });
        }
    };

    const handleMinusAttrCount = (id) => {
        const index = attributeFields.findIndex(
            field => Number(field.attribute_id) === Number(id)
        );
        if (index !== -1) {
            const currentQty = Number(attributeFields[index].quantity) || 1;
            if (currentQty > 1) {
                handleFormChange(index, {
                    target: { name: "quantity", value: (currentQty - 1).toString() }
                });
            }
        }
    };

    return (
        <div className="space-y-4">
            <p className="text-sm text-[#676565] dark:text-[#BCBCBC] mb-4">
                {merchandise?.merchandise_description}
            </p>
            {Object.keys(group).length > 0 &&
                Object.keys(group).map((single, i) => (
                    <div key={i} className="mt-4">
                        <p className="text-left text-lg font-medium text-[#151515] dark:text-white mb-1">
                            Choose {single}
                        </p>
                        {group[single]?.map((attribute, idx) => (
                            <ul key={idx} className="mb-2">
                                <li className="bg-white dark:bg-[#121212]">
                                    <div className="flex items-center justify-between gap-3 py-2">
                                        <label
                                            htmlFor={`custom-checkbox-${attribute.id}`}
                                            className="text-sm text-[#676565] dark:text-[#BCBCBC] cursor-pointer"
                                        >
                                            {attribute?.name} (+NGN {numberWithCommas(attribute?.amount)})
                                        </label>

                                        <div className="flex items-center justify-end gap-4">
                                            {hasAttributeId(attribute?.id) &&
                                                selectedAttr
                                                    .filter(
                                                        (attr) =>
                                                            Number(attr.attribute_id) === Number(attribute?.id)
                                                    )
                                                    .map((attr) => (
                                                        <div
                                                            key={attr.attribute_id}
                                                            className="inline-flex gap-3 justify-around items-center rounded-md bg-white dark:bg-transparent text-sm"
                                                        >
                                                            <button
                                                                type="button"
                                                                className="font-bold cursor-pointer bg-primary w-[24px] h-[24px] rounded-full text-white disabled:opacity-50"
                                                                onClick={() =>
                                                                    handleMinusAttrCount(attribute?.id)
                                                                }
                                                                disabled={Number(attr.quantity) <= 1}
                                                            >
                                                                -
                                                            </button>
                                                            <p className="text-[#151515] dark:text-white min-w-[20px] text-center">
                                                                {attr.quantity}
                                                            </p>
                                                            <button
                                                                type="button"
                                                                className="font-bold cursor-pointer bg-primary w-[24px] h-[24px] rounded-full text-white"
                                                                onClick={() =>
                                                                    handleAddAttrCount(attribute?.id)
                                                                }
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    ))}

                                            <input
                                                type="checkbox"
                                                id={`custom-checkbox-${attribute.id}`}
                                                name={attribute.id}
                                                checked={hasAttributeId(attribute?.id)}
                                                onChange={(e) => handleChange(e, attribute)}
                                                className="w-4 h-4 text-primary bg-white dark:bg-transparent hover:bg-primary checked:bg-primary cursor-pointer focus:ring-0"
                                            />
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        ))}
                    </div>
                ))}
        </div>
    );
}
